interface Item {
  id: string;
}

const isNonNullObject = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null;
};

const isArrayOfObjects = (value: unknown): value is Record<string, unknown>[] => {
  return Array.isArray(value) && value.every((item) => isNonNullObject(item));
};

const isArrayOfStrings = (value: unknown): value is string[] => {
  return Array.isArray(value) && value.every((item) => typeof item === 'string');
};

export const asObjectOrNull = <T>(value: T | T[] | string | string[] | null): T | null => {
  if (!isNonNullObject(value)) {
    return null;
  }

  return value as T;
};

export const asObjectArray = <T>(value: T | T[] | string | string[] | null): T[] => {
  if (!isArrayOfObjects(value)) {
    return [];
  }

  return value as T[];
};

export const asNonNullArray = <T>(value: T | (T | null)[] | null): T[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  if (value.some((item) => item === null)) {
    return [];
  }

  return value as T[];
};

export const asStringOrNull = <T>(value: T | T[] | string | string[] | null): string | null => {
  return typeof value === 'string' ? value : null;
};

export const asStringArray = <T>(value: T | T[] | string | string[] | null): string[] => {
  if (!isArrayOfStrings(value)) {
    return [];
  }

  return value;
};

export const findSelectedById = <T extends Item>(
  items: T[],
  value: T | string | null,
): T | null => {
  const itemId = asStringOrNull<T>(value);

  if (itemId === null) {
    return null;
  }

  return items.find((item) => item.id === itemId) || null;
};

export const findSelectedByObject = <T extends Item>(
  items: T[],
  value: T | string | null,
): T | null => {
  const selectedObject = asObjectOrNull<T>(value);

  if (!selectedObject) {
    return null;
  }

  return items.find((item) => item.id === selectedObject.id) || selectedObject;
};

export const findSelectedManyByIds = <T extends Item>(items: T[], value: T[] | string[]): T[] => {
  const itemIds = asStringArray<T>(value);

  if (itemIds.length === 0) {
    return [];
  }

  return items.filter((item) => itemIds.includes(item.id));
};

export const findSelectedManyByObjects = <T extends Item>(value: T[] | string[]): T[] => {
  return asObjectArray<T>(value);
};
